import { AccessSource, User } from '@prisma/client';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { CommonFindResult } from '@app/infra-interfaces';
import { AgsRequestStatus, AgsRoleRoo, FindRolesArgs } from '@app/integ-ags-interface';
import { LdapRoo } from '@app/integ-ldap-interface';
import { Axios } from '@app/web-components';

const ACCESS_SOURCES_ALL_QUERY_KEY = 'ACCESS_SOURCES_ALL_QUERY_KEY';

const accessSourceKeys = {
  all: [ACCESS_SOURCES_ALL_QUERY_KEY] as const,
  lists: () => [...accessSourceKeys.all, 'list'] as const,
  list: (filters: object) => [...accessSourceKeys.lists(), filters] as const,
  item: (accessSource: AccessSource | '', id: string) => [...accessSourceKeys.all, accessSource, id] as const,
  members: (type: AccessSource | undefined, id?: string, params?: object) =>
    [...accessSourceKeys.all, type, 'members', id, params] as const,
  member: (enterpriseId: string | undefined, externalId: string | undefined, type: AccessSource | undefined) =>
    [accessSourceKeys.all, 'member', enterpriseId, externalId, type] as const,
  facelessMembers: (type: AccessSource | undefined, id?: string) =>
    [...accessSourceKeys.all, type, 'faceless-members', id] as const,
};

const getAgsPathBySource = (source: AccessSource | null | undefined) => {
  switch (source) {
    case AccessSource.AGS_ROLE:
      return '/ags/roles';
    case AccessSource.AGS_ENTITLEMENT:
      return '/ags/entitlements';
    default:
      throw new Error(`AccessSource '${source}' is not supported.`);
  }
};

export const useGetMembershipStatus = (
  enterpriseId: string | undefined,
  externalId: string | undefined,
  source: AccessSource | undefined,
) => {
  const axios = Axios.useAxiosApi();
  const path = getAgsPathBySource(source);

  return useQuery<AgsRequestStatus | undefined>({
    queryKey: accessSourceKeys.member(enterpriseId, externalId, source),
    queryFn: async () => {
      const { data } = await axios.get(`${path}/${externalId}/members/${enterpriseId}`);
      return data;
    },
    enabled: !!enterpriseId && !!externalId && !!source && source !== AccessSource.IEM,
  });
};

export const useFindAgsAccesses = (source: AccessSource, params: FindRolesArgs) => {
  const axios = Axios.useAxiosApi();
  const path = getAgsPathBySource(source);

  return useQuery<CommonFindResult<AgsRoleRoo>>({
    queryKey: accessSourceKeys.list({ source, ...params }),
    queryFn: async () => {
      const { data } = await axios.get(path, { params });
      return data;
    },
  });
};

export const useFindIemGroup = (commonName?: string) => {
  const axios = Axios.useAxiosApi();

  return useQuery<LdapRoo[]>({
    queryKey: accessSourceKeys.list({ source: AccessSource.IEM, name: commonName }),
    queryFn: async () => {
      const { data } = await axios.get(`/ldap/iem-group-find/${commonName}`);
      return data;
    },
    enabled: !!commonName,
  });
};

export const useFindAccessFromRoo = (
  externalId: string | null | undefined,
  source: AccessSource | null | undefined,
  options?: UseQueryOptions<AgsRoleRoo | LdapRoo>,
) => {
  const axios = Axios.useAxiosApi();
  const url = source === AccessSource.IEM ? '/ldap/iem-group-find' : getAgsPathBySource(source);

  return useQuery<AgsRoleRoo | LdapRoo>({
    queryKey: accessSourceKeys.item(source ?? '', externalId ?? ''),
    queryFn: async () => {
      const { data } = await axios.get(
        `${url}/${externalId}`,
        source === AccessSource.IEM ? { params: { exactMatch: true } } : undefined,
      );
      return source === AccessSource.IEM ? data[0] : data;
    },
    ...options,
    enabled: !!externalId && !!source && (options?.enabled ?? true),
  });
};

export type AgsMember = { idsid: string; enterpriseId: string; memberType: string };

export const useFindAgsMembership = (
  accessSource: AccessSource | undefined,
  externalId: string | undefined,
  params?: { identityType?: string; sort?: string },
) => {
  const axios = Axios.useAxiosApi();
  const path = getAgsPathBySource(accessSource);

  return useQuery<AgsMember[]>({
    queryKey: accessSourceKeys.members(accessSource, externalId, params),
    queryFn: async () => {
      const { data } = await axios.get(`${path}/${externalId}/members`, { params });
      return data;
    },
    enabled: !!externalId && !!accessSource,
  });
};

export const useFindAccessFacelessAuditMembers = (
  accessSource: AccessSource | undefined,
  externalId: string | undefined,
) => {
  const axios = Axios.useAxiosApi();
  const path = getAgsPathBySource(accessSource);

  return useQuery<{ access: AgsRoleRoo; user: User; isValid: boolean }[]>({
    queryKey: accessSourceKeys.facelessMembers(accessSource, externalId),
    queryFn: async () => {
      const accessMembers: AgsMember[] = await axios.get(`${path}/${externalId}/members`).then((result) => result.data);

      const facelessAccountAccesses: AgsRoleRoo[] = await Promise.all(
        accessMembers
          .filter((x) => x.memberType.toLowerCase() === 'generic')
          .map((account) =>
            axios
              .get(`/ags/roles`, { params: { name: `${account.idsid} generic account access` } })
              .then((result) =>
                result.data.data.find((x: AgsRoleRoo) => x.name === `${account.idsid} generic account access`),
              ),
          ),
      );

      const memberResults: { access: AgsRoleRoo; members: AgsMember[] }[] = await Promise.all(
        facelessAccountAccesses.map((access) =>
          axios
            .get(
              `/ags/${access.accessSource === AccessSource.AGS_ROLE ? 'roles' : 'entitlements'}/${access.externalId}/members`,
            )
            .then((result) => ({ access, members: result.data })),
        ),
      );

      const facelessMembers = memberResults.flatMap(({ access, members }) =>
        members.map((member) => ({
          access,
          member,
          isValid: accessMembers.some((x) => x.enterpriseId === member.enterpriseId),
        })),
      );

      return Promise.all(
        facelessMembers.map(({ access, member, isValid }) =>
          axios
            .get('/user/', { params: { enterpriseId: member.enterpriseId } })
            .then((response) => ({ access, user: response.data, isValid })),
        ),
      );
    },
    enabled: !!externalId && !!accessSource,
  });
};
